import { ChangeDetectorRef, Component, forwardRef, Inject, OnInit } from '@angular/core';
import { DestroySubscribers } from '../../../decorators/destroy-subscribers.decorator';
import { Observable, Subscription } from 'rxjs';
import {
  ControlValueAccessor,
  UntypedFormControl,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { IEnvironmentService } from '../../../services/environment/environment.interface';
import { filter, map, pluck, tap } from 'rxjs/operators';
import { ADVERTISER_CONDITIONS_TRANSLATIONS } from './translations.const';

@Component({
  templateUrl: './advertiser-conditions.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AdvertiserConditionsComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AdvertiserConditionsComponent),
      multi: true,
    },
  ],
})
@DestroySubscribers()
export class AdvertiserConditionsComponent implements OnInit, ControlValueAccessor, Validator {
  constructor(
    private cd: ChangeDetectorRef,
    private http: HttpClient,
    @Inject('IEnvironmentService') private envService: IEnvironmentService
  ) {
    this.api = this.envService.getVar('services.api') as string;
  }

  public form = new UntypedFormGroup({});
  public subscribers: Record<string, Subscription> = {};
  public data: Observable<any>;
  public advertiserId: Observable<number>;
  public isLoading = true;
  private api = '';
  private hasSettings = false;
  private onChange: (value: any) => void = () => {};
  private onTouch = () => {};

  ngOnInit(): void {
    this.subscribers.form = this.form.valueChanges.subscribe((items) => {
      this.onChange(JSON.stringify(items));
      this.onTouch();
    });
    this.subscribers.advertiserId = this.advertiserId.subscribe((id) => {
      this.isLoading = true;
      this.form.updateValueAndValidity();
      this.data = this.http.get(this.api + '/promotions/' + id).pipe(
        pluck<{ data: IData }, 'data'>('data'),
        tap((data: IData) => this.translate(data)),
        map((data) => Object.entries(data).map(([key, value]) => ({ key, value }))),
        tap((data) => {
          this.isLoading = false;
          this.hasSettings = !!data.length;
          this.form.updateValueAndValidity();
          this.cd.detectChanges();
        }),
        filter((data) => !!data.length),
        tap((data) =>
          data.forEach(({ key }) => {
            this.addForm(key);
          })
        )
      );
    });
  }

  public addForm(key: string): void {
    this.form.addControl(key, new UntypedFormControl(null, Validators.required));
  }

  public writeValue(obj: any): void {
    try {
      const value: IData = JSON.parse(obj);
      Object.keys(value).forEach((key) => this.addForm(key));
      this.form.patchValue(value);
      this.cd.detectChanges();
    } catch {}
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  public validate(): ValidationErrors | null {
    switch (true) {
      case this.form.invalid:
      case this.isLoading:
      case !this.hasSettings:
        return { errors: true };
      default:
        return null;
    }
  }

  public translate(data: IData): void {
    Object.keys(data).forEach((key) => {
      data[key].label = ADVERTISER_CONDITIONS_TRANSLATIONS[key]?.label ?? data[key].label;
      data[key].tooltip = ADVERTISER_CONDITIONS_TRANSLATIONS[key]?.tooltip ?? data[key].tooltip;
    });
  }
}

interface IData {
  [key: string]: {
    type: 'string';
    label: string;
    placeholder: string;
    tooltip: string;
  };
}
